import {
  DashboardPage,
  AccountSettingsPage,
  SubscriptionPage,
  NotFoundPage,
  ServerErrorPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  UserInvitationsPage,
  AcceptInvitationPage,
  UsersPage,
  FormsPage,
  SigninPage,
  SignupPage,
  SubmissionsPage,
  AccountEmailsPage,
  ConnectorsPage,
  LookupDictionariesPage,
  LookupDictionaryItemsPage,
  EmailLayoutsPage,
  EmailTemplatesPage,
  PrintSubmissionPage,
  SandboxPage,
  WorkflowsPage,
  WorkflowItemsPage,
  UserProfilePage,
} from "@/pages";
import { DashboardLayout } from "@/components/layouts";
import { ALL_ROLES, MANAGER_ROLES, USER_ROLES } from "./userRoles";

export interface SiteMapItem {
  name: string;
  subtitle?: string;
  url?: string;
  urlMask?: string;
  component?: React.ComponentType<any>;
  layout?: React.ComponentType<any>;
  visible?: boolean;
  roles?: string[];
  items?: SiteMapItem[];
  options?: any;
  icon?: string; // Icon name for navigation
}

export const componentsMap = {
  // Pages
  DashboardPage,
  AccountSettingsPage,
  SubscriptionPage,
  NotFoundPage,
  ServerErrorPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  UserInvitationsPage,
  AcceptInvitationPage,
  UsersPage,
  FormsPage,
  SigninPage,
  SignupPage,
  // Layouts
  DashboardLayout,
};

const siteMap: SiteMapItem[] = [
  // Private Pages
  {
    name: "Dashboard",
    subtitle: "Your FormSubmits control center",
    url: "/",
    component: DashboardPage,
    visible: true,
    roles: ALL_ROLES,
    items: [],
    options: {},
    icon: "Dashboard",
  },
  {
    name: "Forms",
    subtitle: "Manage form configurations and fields",
    url: "/forms",
    component: FormsPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Add Form",
        url: "/forms/add",
        component: FormsPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.MEMBER],
        items: [],
        options: {},
      },
      {
        name: "Edit Form",
        url: "/forms/:formId/edit",
        component: FormsPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {},
      },
      {
        name: "Edit Form Fields",
        url: "/forms/:formId/edit-fields",
        component: FormsPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {},
      },
      {
        name: "Get Form Code",
        url: "/forms/:formId/code",
        component: FormsPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {},
      },
    ],
    options: {},
    icon: "FileText",
  },

  // Submissions
  {
    name: "Submissions",
    subtitle: "View and manage form submissions",
    url: "/submissions",
    component: SubmissionsPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Add Submission",
        url: "/submissions/add",
        component: SubmissionsPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.MEMBER],
        items: [],
        options: {},
      },
      {
        name: "View Submission",
        url: "/submissions/:submissionId/view",
        component: SubmissionsPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {},
      },
      {
        name: "Print Submission",
        url: "/submissions/:submissionId/print",
        component: PrintSubmissionPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {},
      },
    ],
    options: {},
    icon: "Inbox",
  },

  // Connectors
  {
    name: "Connectors",
    subtitle: "Manage your connectors (storage, messengers, crms, etc)",
    url: "/connectors",
    component: ConnectorsPage,
    visible: true,
    roles: [...MANAGER_ROLES, USER_ROLES.MEMBER, USER_ROLES.VIEWER],
    items: [
      {
        name: "Add Connector",
        subtitle: "Add a new connector",
        url: "/connectors/add",
        component: ConnectorsPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.MEMBER],
        items: [],
        options: {},
        icon: "PlugConnected",
      },
      {
        name: "Edit Connector",
        subtitle: "Edit connector",
        url: "/connectors/:id/edit",
        component: ConnectorsPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {},
        icon: "PlugConnected",
      },
    ],
    options: {},
    icon: "PlugConnected",
  },

  // Lookup Dictionaries
  {
    name: "Lookup Dictionaries",
    subtitle: "Manage your lookup dictionaries",
    url: "/lookup-dictionaries",
    component: LookupDictionariesPage,
    visible: true,
    roles: ALL_ROLES,
    items: [
      {
        name: "Add Lookup Dictionary",
        subtitle: "",
        url: "/lookup-dictionaries/add",
        component: LookupDictionariesPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.MEMBER],
        items: [],
        options: {},
        icon: "Vocabulary",
      },
      {
        name: "Edit Lookup Dictionary",
        subtitle: "",
        url: "/lookup-dictionaries/:id/edit",
        component: LookupDictionariesPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {},
        icon: "Vocabulary",
      },
    ],
    options: {},
    icon: "Vocabulary",
  },

  // Lookup Dictionary Items
  {
    name: "Lookup Dictionary Items",
    subtitle: "Manage your lookup dictionaries",
    url: "/lookup-dictionaries/:id/items",
    component: LookupDictionaryItemsPage,
    visible: false,
    roles: ALL_ROLES,
    items: [
      {
        name: "Add Lookup Dictionary Item",
        subtitle: "",
        url: "/lookup-dictionaries/:id/items/add",
        component: LookupDictionaryItemsPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.MEMBER],
        items: [],
        options: {},
        icon: "Vocabulary",
      },
      {
        name: "Edit Lookup Dictionary Item",
        subtitle: "",
        url: "/lookup-dictionaries/:id/items/:itemId/edit",
        component: LookupDictionaryItemsPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {},
        icon: "Vocabulary",
      },
    ],
    options: {},
    icon: "Vocabulary",
  },

  // Workflows
  {
    name: "Workflows",
    subtitle: "Manage your workflows",
    url: "/workflows",
    component: WorkflowsPage,
    visible: true,
    roles: ALL_ROLES,
    icon: "ArrowsSplit",
    options: {},
    items: [
      {
        // Opens the drawer in create mode
        // Condition: location.pathname.endsWith("/add")
        name: "Add Workflow",
        subtitle: "",
        url: "/workflows/add",
        component: WorkflowsPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.MEMBER],
        icon: "ArrowsSplit",
        options: {},
        items: [],
      },
      {
        // Opens the drawer in edit mode
        // Condition: params.id + location.pathname.endsWith("/edit")
        name: "Edit Workflow",
        subtitle: "",
        url: "/workflows/:id/edit",
        component: WorkflowsPage,
        visible: false,
        roles: ALL_ROLES,
        icon: "ArrowsSplit",
        options: {},
        items: [],
      },
      {
        // Workflow items list — reached via the Go In action on a workflow row.
        // params.workflowId scopes the list to a single workflow.
        name: "Workflow Items",
        subtitle: "Manage items for a workflow",
        url: "/workflows/:workflowId/items",
        component: WorkflowItemsPage,
        visible: false,
        roles: ALL_ROLES,
        icon: "Route",
        options: {},
        items: [
          {
            // Opens the item create drawer
            // Condition: location.pathname.endsWith("/add")
            name: "Add Workflow Item",
            subtitle: "",
            url: "/workflows/:workflowId/items/add",
            component: WorkflowItemsPage,
            visible: false,
            roles: [...MANAGER_ROLES, USER_ROLES.MEMBER],
            icon: "Route",
            options: {},
            items: [],
          },
          {
            // Opens the item edit drawer
            // Condition: params.itemId + location.pathname.endsWith("/edit")
            name: "Edit Workflow Item",
            subtitle: "",
            url: "/workflows/:workflowId/items/:itemId/edit",
            component: WorkflowItemsPage,
            visible: false,
            roles: ALL_ROLES,
            icon: "Route",
            options: {},
            items: [],
          },
        ],
      },
    ],
  },

  {
    name: "Emails",
    subtitle: "",
    url: "",
    visible: true,
    roles: ALL_ROLES,
    items: [
      // Account emails
      {
        name: "Email Addresses",
        subtitle: "Manage emails to be used in submission notifications",
        url: "/account-emails",
        component: AccountEmailsPage,
        visible: true,
        roles: ALL_ROLES,
        items: [],
        options: {},
        icon: "MailSpark",
      },
      {
        name: "Add an Email Address",
        subtitle: "",
        url: "/account-emails/add",
        component: AccountEmailsPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.MEMBER, USER_ROLES.VIEWER],
        items: [],
        options: {},
        icon: "MailSpark",
      },
      {
        name: "Edit an Email Address",
        subtitle: "",
        url: "/account-emails/:id/edit",
        component: AccountEmailsPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {},
        icon: "MailSpark",
      },
      {
        name: "Confirm Email Address",
        subtitle: "",
        url: "/account-emails/:id/confirm",
        component: AccountEmailsPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.MEMBER],
        items: [],
        options: {},
        icon: "PlugConnected",
      },

      // Email Layouts
      {
        name: "Email Layouts",
        subtitle: "Manage email layouts",
        url: "/email-layouts",
        component: EmailLayoutsPage,
        visible: true,
        roles: [...MANAGER_ROLES, USER_ROLES.SUPPORT_AGENT, USER_ROLES.VIEWER],
        items: [],
        options: {},
        icon: "Layout",
      },
      {
        name: "Add Email Layout",
        url: "/email-layouts/add",
        component: EmailLayoutsPage,
        visible: false,
        roles: MANAGER_ROLES,
        items: [],
        options: {},
        icon: "Layout",
      },
      {
        name: "Edit Email Layout",
        url: "/email-layouts/:emailLayoutId/edit",
        component: EmailLayoutsPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.SUPPORT_AGENT, USER_ROLES.VIEWER],
        items: [],
        options: {},
        icon: "Layout",
      },
      {
        name: "Preview Email Layout",
        url: "/email-layouts/:emailLayoutId/preview",
        component: EmailLayoutsPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.SUPPORT_AGENT, USER_ROLES.VIEWER],
        items: [],
        options: {},
        icon: "Layout",
      },

      // Email Templates
      {
        name: "Email Templates",
        subtitle: "Manage email templates",
        url: "/email-templates",
        component: EmailTemplatesPage,
        visible: true,
        roles: ALL_ROLES,
        items: [],
        options: {},
        icon: "Template",
      },
      {
        name: "Add Email Template",
        url: "/email-templates/add",
        component: EmailTemplatesPage,
        visible: false,
        roles: [...MANAGER_ROLES, USER_ROLES.MEMBER],
        items: [],
        options: {},
        icon: "Template",
      },
      {
        name: "Edit Email Template",
        url: "/email-templates/:emailTemplateId/edit",
        component: EmailTemplatesPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {},
        icon: "Template",
      },
      {
        name: "Preview Email Template",
        url: "/email-templates/:emailTemplateId/preview",
        component: EmailTemplatesPage,
        visible: false,
        roles: ALL_ROLES,
        items: [],
        options: {},
        icon: "Template",
      },
    ],
    options: {},
    icon: "MailSpark",
  },

  // Account pages
  {
    name: "Account",
    visible: true,
    roles: [
      USER_ROLES.OWNER,
      USER_ROLES.SUPPORT_AGENT,
      USER_ROLES.ADMIN,
      USER_ROLES.VIEWER,
    ],
    items: [
      {
        name: "User Invitations",
        subtitle: "Manage invitations to your account",
        url: "/account/users/invitations",
        component: UserInvitationsPage,
        visible: true,
        roles: [USER_ROLES.OWNER, USER_ROLES.SUPPORT_AGENT, USER_ROLES.ADMIN],
        items: [],
        options: {},
        icon: "User",
      },
      {
        name: "Invite User",
        subtitle: "Invite new user to be part of your team",
        url: "/account/users/invitations/add",
        component: UserInvitationsPage,
        visible: false,
        roles: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
        items: [],
        options: {},
        icon: "User",
      },
      {
        name: "Edit Invitation",
        subtitle: "",
        url: "/account/users/invitations/:userInvitationId/edit",
        component: UserInvitationsPage,
        visible: false,
        roles: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
        items: [],
        options: {},
        icon: "User",
      },
      {
        name: "Users",
        subtitle: "Manage users of your account",
        url: "/account/users",
        component: UsersPage,
        visible: true,
        roles: [
          USER_ROLES.OWNER,
          USER_ROLES.ADMIN,
          USER_ROLES.SUPPORT_AGENT,
          USER_ROLES.VIEWER,
        ],
        items: [],
        options: {},
        icon: "User",
      },
      {
        name: "Edit User",
        subtitle: "",
        url: "/account/users/:userId/edit",
        component: UsersPage,
        visible: false,
        roles: [USER_ROLES.OWNER, USER_ROLES.ADMIN],
        items: [],
        options: {},
        icon: "User",
      },
      {
        name: "Account Settings",
        subtitle: "Manage your account settings",
        url: "/account/settings",
        component: AccountSettingsPage,
        visible: false,
        roles: [USER_ROLES.OWNER, USER_ROLES.ADMIN, USER_ROLES.VIEWER],
        items: [],
        options: {},
        icon: "Settings",
      },
      {
        name: "Subscription",
        subtitle: "Manage your subscription",
        url: "/account/subscription",
        component: SubscriptionPage,
        visible: false,
        roles: [USER_ROLES.OWNER],
        items: [],
        options: {},
        icon: "CreditCard",
      },
    ],
    options: {},
    icon: "User",
  },

  {
    name: "Sandbox",
    subtitle: "Get your form HTML template",
    url: "/sandbox",
    component: SandboxPage,
    visible: true,
    roles: ALL_ROLES,
    items: [],
    options: {},
    icon: "Sandbox",
  },

  /* **********************************************************
   * Public Pages
   * ********************************************************** */
  {
    name: "Sign Up",
    subtitle: "Create your FormSubmits account",
    url: "/account/signup",
    component: SignupPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  {
    name: "Sign In",
    subtitle: "Sign in to your FormSubmits account",
    url: "/account/signin",
    component: SigninPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  {
    name: "User Profile",
    subtitle: "Manage your profile information",
    url: "/account/profile",
    component: UserProfilePage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  {
    name: "Forgot Password",
    subtitle: "Request password reset link",
    url: "/account/forgot-password",
    component: ForgotPasswordPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  {
    name: "Reset Password",
    subtitle: "Reset password",
    url: "/account/reset-password",
    component: ResetPasswordPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  {
    name: "Accept Invitation",
    subtitle: "Accept invitation to join company at FormSubmits",
    url: "/account/accept-invitation",
    component: AcceptInvitationPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  {
    name: "Server Error Page",
    subtitle: "",
    url: "/server-error",
    component: ServerErrorPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
  // Not Found
  {
    name: "Page Not Found",
    subtitle: "",
    url: "*",
    component: NotFoundPage,
    visible: false,
    roles: [USER_ROLES.NO_ROLE],
    items: [],
    options: {},
  },
];

export default siteMap;
